import React, { useState, useEffect } from 'react';
import { X, Calendar, MapPin, Plus, Home, Target, ChevronRight, Users } from 'react-feather';
import { tournamentAPI, Tournament } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import TournamentDetailModal from './TournamentDetailModal';
import AddEditTournamentModal from './AddEditTournamentModal';
import { useResponsive } from '../hooks/useResponsive';

interface TournamentListModalProps {
  athleteId: string;
  onClose: () => void;
  onUpdate: () => void;
}

const TournamentListModal: React.FC<TournamentListModalProps> = ({
  athleteId,
  onClose,
  onUpdate,
}) => {
  const { sessionToken } = useAuth();
  const toast = useToast();
  const { isMobile } = useResponsive();
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'upcoming' | 'past' | 'all'>('upcoming');
  const [selectedTournament, setSelectedTournament] = useState<Tournament | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [editingTournament, setEditingTournament] = useState<Tournament | null>(null);

  useEffect(() => {
    if (sessionToken && athleteId) {
      // loadTournaments only ever sets loading=false; set it true here so a
      // filter change shows the loading state instead of stale results.
      setLoading(true);
      loadTournaments();
    }
  }, [sessionToken, athleteId, filter]);

  const loadTournaments = async (): Promise<Tournament[]> => {
    if (!sessionToken || !athleteId) return [];

    try {
      const response = await tournamentAPI.getTournaments(
        sessionToken,
        athleteId,
        filter
      );

      if (response.success) {
        setTournaments(response.data.tournaments);
        return response.data.tournaments;
      }
      return [];
    } catch (error) {
      console.error('Error loading tournaments:', error);
      toast.error('Failed to load tournaments');
      return [];
    } finally {
      setLoading(false);
    }
  };

  const handleTournamentClick = (tournament: Tournament) => {
    setSelectedTournament(tournament);
    setShowDetailModal(true);
  };

  const handleAddTournament = () => {
    setEditingTournament(null);
    setShowAddEditModal(true);
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setSelectedTournament(null);
  };

  const handleTournamentUpdated = async () => {
    const updated = await loadTournaments();
    onUpdate();
    
    // Update the selected tournament if we're viewing detail
    if (selectedTournament && showDetailModal) {
      const updatedTournament = updated.find(t => t.id === selectedTournament.id);
      if (updatedTournament) {
        setSelectedTournament(updatedTournament);
      }
    }
  };

  const formatDateRange = (startDate: string, endDate: string): string => {
    const start = new Date(startDate.split('T')[0] + 'T00:00:00');
    const end = new Date(endDate.split('T')[0] + 'T00:00:00');
    
    if (start.toDateString() === end.toDateString()) {
      return start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
    
    if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
      return `${start.toLocaleDateString('en-US', { month: 'short' })} ${start.getDate()}-${end.getDate()}, ${start.getFullYear()}`;
    }
    
    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };

  return (
    <>
      <div style={styles.overlay} onClick={onClose}>
        <div style={{
          ...styles.modal,
          maxWidth: isMobile ? 'calc(100vw - 32px)' : '900px',
          maxHeight: isMobile ? 'calc(100vh - 100px)' : '90vh',
          width: isMobile ? 'calc(100vw - 32px)' : '100%',
        }} onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div style={{
            ...styles.header,
            padding: isMobile ? '16px' : '24px',
          }}>
            <div style={styles.headerLeft}>
              <Calendar size={24} style={{ color: '#F9550D' }} />
              <h2 style={styles.title}>Tournament Schedule</h2>
            </div>
            <button onClick={onClose} style={styles.closeButton}>
              <X size={24} />
            </button>
          </div>

          {/* Filter Tabs */}
          <div style={styles.filterContainer}>
            <button
              style={{
                ...styles.filterTab,
                ...(filter === 'upcoming' ? styles.filterTabActive : {}),
              }}
              onClick={() => setFilter('upcoming')}
            >
              Upcoming
            </button>
            <button
              style={{
                ...styles.filterTab,
                ...(filter === 'past' ? styles.filterTabActive : {}),
              }}
              onClick={() => setFilter('past')}
            >
              Past
            </button>
            <button
              style={{
                ...styles.filterTab,
                ...(filter === 'all' ? styles.filterTabActive : {}),
              }}
              onClick={() => setFilter('all')}
            >
              All
            </button>
          </div>

          {/* Content */}
          <div style={styles.content}>
            {loading ? (
              <div style={styles.loadingContainer}>
                <div style={styles.spinner} />
                <p style={styles.loadingText}>Loading tournaments...</p>
              </div>
            ) : tournaments.length === 0 ? (
              <div style={styles.emptyState}>
                <Calendar size={64} color="#D1D5DB" />
                <h3 style={styles.emptyTitle}>No Tournaments Yet</h3>
                <p style={styles.emptyText}>
                  {filter === 'upcoming'
                    ? 'Add your first upcoming tournament!'
                    : filter === 'past'
                    ? 'No past tournaments found'
                    : 'Start by adding a tournament'}
                </p>
                <button onClick={handleAddTournament} style={styles.addButtonLarge}>
                  <Plus size={20} />
                  Add Tournament
                </button>
              </div>
            ) : (
              <div style={styles.tournamentGrid}>
                {tournaments.map((tournament) => (
                  <div
                    key={tournament.id}
                    style={styles.tournamentCard}
                    onClick={() => handleTournamentClick(tournament)}
                  >
                    <div style={styles.cardHeader}>
                      <h3 style={styles.tournamentName}>{tournament.name}</h3>
                      <ChevronRight size={20} style={{ color: '#F9550D' }} />
                    </div>

                    {tournament.facility_name && (
                      <div style={styles.infoRow}>
                        <Home size={14} style={styles.icon} />
                        <span style={styles.infoText}>{tournament.facility_name}</span>
                      </div>
                    )}

                    <div style={styles.infoRow}>
                      <MapPin size={14} style={styles.icon} />
                      <span style={styles.infoText}>
                        {tournament.city}, {tournament.state}
                      </span>
                    </div>

                    <div style={styles.infoRow}>
                      <Calendar size={14} style={styles.icon} />
                      <span style={styles.infoText}>
                        {formatDateRange(tournament.start_date, tournament.end_date)}
                      </span>
                    </div>

                    {tournament.games && tournament.games.length > 0 && (
                      <div style={styles.infoRow}>
                        <Target size={14} style={styles.icon} />
                        <span style={styles.infoText}>
                          {tournament.games.length} game{tournament.games.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                    )}

                    {/* KGH-795 / APS-255 item 11. The one fact on this card that
                        is worth opening it for, so it is on the card. */}
                    {!!tournament.coaches_attending && tournament.coaches_attending > 0 && (
                      <div style={styles.infoRow}>
                        <Users size={14} style={{ ...styles.icon, color: 'var(--aps-action)' }} />
                        <span style={{ ...styles.infoText, color: 'var(--aps-action)', fontWeight: 700 }}>
                          {tournament.coaches_attending} college program
                          {tournament.coaches_attending === 1 ? '' : 's'} going
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer with Add Button */}
          <div style={styles.footer}>
            <button onClick={handleAddTournament} style={styles.addButton}>
              <Plus size={20} />
              Add Tournament
            </button>
          </div>
        </div>
      </div>

      {showDetailModal && selectedTournament && (
        <TournamentDetailModal
          tournament={selectedTournament}
          onClose={handleCloseDetailModal}
          onUpdate={async () => {
            await handleTournamentUpdated();
          }}
        />
      )}

      {showAddEditModal && (
        <AddEditTournamentModal
          athleteId={athleteId}
          tournament={editingTournament}
          onClose={() => {
            setShowAddEditModal(false);
            setEditingTournament(null);
          }}
          onSuccess={handleTournamentUpdated}
        />
      )}
    </>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px',
  },
  modal: {
    backgroundColor: '#FFFFFF',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '900px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '24px',
    borderBottom: '1px solid #E5E7EB',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  title: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1F2937',
    margin: 0,
    fontFamily: 'Manrope, sans-serif',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '8px',
    color: '#6B7280',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '8px',
    transition: 'all 0.2s',
  },
  filterContainer: {
    display: 'flex',
    gap: '8px',
    padding: '16px 24px',
    borderBottom: '1px solid #E5E7EB',
  },
  filterTab: {
    padding: '8px 16px',
    backgroundColor: 'transparent',
    border: '1px solid #E5E7EB',
    borderRadius: '8px',
    color: '#6B7280',
    fontSize: '14px',
    fontWeight: '600',
    fontFamily: 'Manrope, sans-serif',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  filterTabActive: {
    backgroundColor: '#F9550D',
    borderColor: '#F9550D',
    color: '#FFFFFF',
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: '24px',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '3px solid #F3F4F6',
    borderTop: '3px solid #F9550D',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    marginTop: '16px',
    color: '#6B7280',
    fontSize: '14px',
    fontFamily: 'Manrope, sans-serif',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    textAlign: 'center',
  },
  emptyTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#1F2937',
    margin: '16px 0 8px 0',
    fontFamily: 'Manrope, sans-serif',
  },
  emptyText: {
    fontSize: '14px',
    color: '#6B7280',
    margin: '0 0 24px 0',
    fontFamily: 'Manrope, sans-serif',
  },
  addButtonLarge: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 24px',
    backgroundColor: '#F9550D',
    border: 'none',
    borderRadius: '8px',
    color: '#FFFFFF',
    fontSize: '16px',
    fontWeight: '600',
    fontFamily: 'Manrope, sans-serif',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  tournamentGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '16px',
  },
  tournamentCard: {
    padding: '20px',
    backgroundColor: '#FFFFFF',
    border: '1px solid #E5E7EB',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '16px',
  },
  tournamentName: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#1F2937',
    margin: 0,
    fontFamily: 'Manrope, sans-serif',
    flex: 1,
  },
  infoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
  },
  icon: {
    color: '#6B7280',
    flexShrink: 0,
  },
  infoText: {
    fontSize: '14px',
    color: '#6B7280',
    fontFamily: 'Manrope, sans-serif',
  },
  footer: {
    padding: '16px 24px',
    borderTop: '1px solid #E5E7EB',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  addButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 20px',
    backgroundColor: '#F9550D',
    border: 'none',
    borderRadius: '8px',
    color: '#FFFFFF',
    fontSize: '14px',
    fontWeight: '600',
    fontFamily: 'Manrope, sans-serif',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
};

export default TournamentListModal;
